<template>
  <div>
    <TopBar />

    <header class="flex flex-col items-center justify-center border-b border-solid border-gray-light pt-6 leading-none">
      <h1 class="mb-8 text-center font-bold text-slate" style="font-size: 39px;">
        <span class="inline-block">
          <svg width="20" height="29" viewBox="0 0 18 29" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path fill-rule="evenodd" clip-rule="evenodd" d="M10.4211 18.4674C14.7164 17.7861 18 14.066 18 9.57898C18 4.60842 13.9706 0.578979 9 0.578979C4.02944 0.578979 0 4.60842 0 9.57898C0 14.066 3.2836 17.7861 7.57895 18.4674V29H10.4211V18.4674Z" fill="#1A1A1A" />
          </svg>
        </span>
        {{ destination.name }}
      </h1>
      <div class="font-bw-gradual flex justify-between px-6">
        <div v-for="type in venueTypes" :key="type.id">
          <NuxtLink
            v-if="destination[`show_${type.permalink}s`]"
            :to="`/${destination.permalink}/${type.permalink}s`"
            class="venue-type mx-2 block text-center sm:mx-4 md:mx-6"
            :class="{ active: venueType.id === type.id }"
            :style="{ paddingBottom: '7px' }"
          >
            <h3 class="text-center text-xl text-gray-mid hover:text-gray-1 md:text-2xl">
              {{ $f.pluralize(type.name) }}
            </h3>
          </NuxtLink>
        </div>
      </div>
    </header>

    <main class="mx-auto px-5 md:pr-0">
      <div>
        <div
          class="mt-10 md:pr-6 lg:w-160"
          :class="{ hidden: showMap && ['sm', 'md'].includes($screen.name) }"
          :style="{ paddingBottom: showMap && ['sm', 'md'].includes($screen.name) ? 'calc(100vh - 250px)' : null }"
        >
          <ClientOnly>
            <VenuesFilters :venue-type="venueType" :destination="destination" />
          </ClientOnly>

          <div class="mb-4 flex justify-between text-gray-800">
            <div class="mt-3 text-sm text-body">
              <span v-if="!loadingVenues && (!firstLoad || totalVenues)">Showing {{ totalVenues }} {{ $f.pluralize(venueType.permalink) }}</span>
            </div>
            <div v-if="venueType.permalink === 'hotel'" class="inline-block text-sm text-body">
              Sort by
              <DropdownSelect v-model="order" :options="orderOptions" mobile-title="Sort by" adaptive>
                <template #button="{ click }">
                  <button class="focused:outline-none mt-3 flex items-center gap-1 text-sm text-body" @click="click">
                    <span class="inline-block underline">{{ orderOptions.find(o => o.id === order).label }}</span>
                    <svg
                      class="mt-1"
                      width="8"
                      height="5"
                      viewBox="0 0 8 5"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path fill-rule="evenodd" clip-rule="evenodd" d="M0 0.649682L0.649682 0L4 3.35032L7.35032 0L8 0.649682L4 4.64968L0 0.649682Z" fill="#808080" />
                    </svg>
                  </button>
                </template>
              </DropdownSelect>
            </div>
          </div>

          <div v-if="loadingVenues" class="mb-10 h-20 spinner" />

          <div v-if="venues.length">
            <div :class="{ 'opacity-50': loadingVenues }">
              <Venue
                v-for="(venue, index) in venues"
                :key="venue.id"
                ref="venues"
                :venue="venue"
                :index="index"
                @click="venueClicked = venue"
                @mouseover="hoveringVenueId = venue.id"
                @mouseleave="hoveringVenueId = null"
              />
            </div>

            <div class="mb-20 mt-8 lg:mb-8">
              <Paginate
                v-if="totalVenues > perPage"
                :value="curPage"
                :page-count="Math.ceil(totalVenues / perPage)"
                :click-handler="paginateClick"
              />
              <div class="mt-5 text-center text-sm text-body">
                {{ (curPage - 1) * perPage + 1 }} - {{ Math.min(curPage * perPage, totalVenues) }} of {{ totalVenues }} results
              </div>
            </div>
          </div>
          <div v-if="!loadingVenues && !venues.length">
            No Venues found
          </div>
        </div>
        <div class="relative">
          <ClientOnly>
            <div :class="{ 'mb-144': showMap }">
              <VenuesMap
                v-if="destination.id"
                v-model:show="showMap"
                :venues="venues"
                :loading-venues="loadingVenues"
                :venue-clicked="venueClicked"
                :hovering-venue-id="hoveringVenueId"
                :destination="destination"
                @marker-clicked="markerClick"
                @search-area="searchArea"
              />
            </div>
          </ClientOnly>
        </div>
      </div>
    </main>

    <MainFooter />
  </div>
</template>

<script>
import { mapActions, mapState } from 'pinia'
import pluralize from 'pluralize'
import { showError, useHead, useNuxtApp, useRoute } from '#imports'
import venueTypes from '~/config/venue-types.json'
import VenuesMap from '~/components/venues/VenuesMap'
import Venue from '~/components/venues/Venue'
import DropdownSelect from '~/components/shared/DropdownSelect'
import VenuesFilters from '~/components/venues/VenuesFilters'
import TopBar from '~/components/layout/TopBar'
import MainFooter from '~/components/layout/MainFooter'
import Paginate from '~/components/shared/Paginate'
import { useDestinationsStore } from '~/stores/destinations'
import { useCurrenciesStore } from '~/stores/currencies'
import { useVenuesStore } from '~/stores/venues'
import { callWithNuxt } from '#app'
import { useLoadDestination } from '~/composables/useLoadDestination'

export default {
  components: { TopBar, VenuesFilters, DropdownSelect, Venue, VenuesMap, MainFooter, Paginate },

  async setup () {
    const { params } = useRoute()
    const app = useNuxtApp()
    const type = params.type.replace(/s$/, '')
    const destinationsStore = useDestinationsStore()
    const venuesStore = useVenuesStore()

    const venueType = venueTypes.find(v => v.permalink === type)
    if (! venueType) {
      return showError({ statusCode: 404, statusMessage: 'Venue type not found' })
    }

    const { destinationUpdated } = await useLoadDestination()

    const venueTypeUpdated = venuesStore.filters.venue_type_id !== venueType.id
    if (destinationUpdated || venueTypeUpdated) {
      // Clear all filters when destination or venue type is changed
      venuesStore.clearFilters()
      venuesStore.updateFilters({ venue_type_id: venueType.id })
    }

    if (! venuesStore.venues.data.length || destinationUpdated || venueTypeUpdated) {
      // Load venues from page 1 and default order if not loadded,
      // destination or venue type was updated
      venuesStore.venues.page = 1
      venuesStore.venues.order = null
      await callWithNuxt(app, () => venuesStore.loadVenues())
    }

    // Head title, description, meta, preload
    const venueTypePlural = pluralize(venueType.name).toLowerCase()
    const description = `Expert reviewers writing for travel guides, magazines and newspapers recommend these ${venueTypePlural} in ${destinationsStore.destination.data.name}. `
    const preload = venuesStore.venues.data
      .map(v => v.photos.length && v.photos[0])
      .filter(p => p && (p.photo_rectangular_webp || p.photo_rectangular))

    callWithNuxt(app, () => useHead({
      title: `${destinationsStore.destination.data.name} ${venueType.name} Reviews | Tripexpert`,
      meta: [
        { hid: 'description', name: 'description', content: description },
        { hid: 'og:description', property: 'og:description', content: description },
      ],

      link: [
        ...preload.map(p => ({ rel: 'preload', href: p.photo_rectangular_webp || p.photo_rectangular, as: 'image' })),
      ],
    }))
  },

  data: () => ({
    venueClicked: null,
    order: null,
    orderOptions: [
      { id: null, label: 'Score' },
      { id: 'price_asc', label: 'Price (low to high)' },
      { id: 'price_desc', label: 'Price (high to low)' },
    ],

    hoveringVenueId: null,
    showMap: false,
    venueTypes,
  }),

  computed: {
    ...mapState(useDestinationsStore, { destination: s => s.destination.data }),
    ...mapState(useCurrenciesStore, { currencies: s => s.currencies.data }),
    ...mapState(useVenuesStore, {
      venues: s => s.venues.data,
      totalVenues: s => s.venues.total,
      loadingVenues: s => s.venues.loading,
      curPage: s => s.venues.page,
      currOrder: s => s.venues.order,
      perPage: s => s.perPage,
      filters: s => s.filters,
      firstLoad: s => s.venues.firstLoad,
    }),

    venueType () {
      const type = this.$route.params.type.replace(/s$/, '')
      return this.venueTypes.find(v => v.permalink === type)
    },
  },

  watch: {
    filters: {
      deep: true,
      handler () {
        useVenuesStore().venues.page = 1
        this.loadVenues()
      },
    },

    order (val) {
      useVenuesStore().venues.order = val
      this.loadVenues()
    },
  },

  created () {
    this.order = this.currOrder
  },

  methods: {
    ...mapActions(useVenuesStore, ['loadVenues', 'updateFilters']),
    ...mapActions(useDestinationsStore, ['loadDestination']),

    markerClick (index) {
      if (! ['sm', 'md'].includes(this.$screen.name)) {
        this.$scrollTo(this.$refs.venues[index].$refs.details, { offset: -100 })
      }
    },

    paginateClick (page) {
      useVenuesStore().venues.page = page
      this.loadVenues()
    },

    searchArea (bounds) {
      useVenuesStore().venues.page = 1
      this.updateFilters(bounds)
      this.loadVenues()
    },
  },
}
</script>

<style lang="scss" scoped>
.venue-type.active {
  > h3 {
    @apply text-gray-1 relative;
    &:after {
      content: ''; bottom: -8px;
      @apply absolute inset-x-0 h-3px bg-gray-dark;
    }
  }
}
</style>
